/**
 * TagPickerDialog · shared batch-tag picker.
 *
 * 2026-07-31 · Used by two entry points:
 *   1) Products List → 批量操作 → 设置 Tag
 *   2) Tag Management → 选中产品 → 批量加标签
 *
 * 2026-06 (revision per user spec):
 *   - Only tags the current user created (or that were shared with them).
 *   - Newest first, max 10 rows, each showing its creation date.
 *   - Inline "增加 Tag" creator.
 *   - Per-tag sharing to other staff users (owner only).
 */
import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/context/LanguageContext";

const MAX_VISIBLE = 10;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

export function TagPickerDialog({ open, onOpenChange, selectedCount, onConfirm, busy = false, excludeTag = null }) {
  const { t } = useLanguage();
  const [tags, setTags] = useState([]);
  const [pick, setPick] = useState("");
  const [query, setQuery] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [shareTag, setShareTag] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/tags", { params: { sort: "newest", mine: true, limit: 50 } });
      setTags(data.tags || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!open) return;
    setPick("");
    setQuery("");
    setNewName("");
    load();
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tags
      .filter((tg) => (excludeTag ? tg.name !== excludeTag : true))
      .filter((tg) => (q ? tg.name.toLowerCase().includes(q) : true))
      .slice(0, MAX_VISIBLE);
  }, [tags, query, excludeTag]);

  const createTag = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await api.post("/tags", { name });
      setNewName("");
      setPick(data.name);
      await load();
      toast.success(t("tags.createdOk"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tags.createFailed"));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-sm" data-testid="tag-picker-dialog">
          <DialogHeader>
            <DialogTitle>
              {t("tags.batchAddTitle")} ({selectedCount})
            </DialogTitle>
          </DialogHeader>

          {/* 增加 Tag */}
          <div className="flex items-center gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createTag(); } }}
              placeholder={t("tags.newTagPlaceholder")}
              className="h-8 text-xs"
              data-testid="tag-picker-new-input"
            />
            <Button
              size="sm"
              className="h-8 gap-1 shrink-0"
              onClick={createTag}
              disabled={!newName.trim() || creating}
              data-testid="tag-picker-add-button"
            >
              <Plus size={13} /> {t("tags.addNew")}
            </Button>
          </div>

          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("tags.searchTag")}
              className="h-8 text-xs pl-7"
              data-testid="tag-picker-search"
            />
          </div>

          <p className="text-[11px] text-muted-foreground">{t("tags.onlyMineHint")}</p>

          <div className="space-y-1 max-h-72 overflow-y-auto -mx-1 px-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2 py-6 text-center">
                {tags.length === 0 ? t("tags.emptyDict") : t("tags.noMatch")}
              </p>
            ) : filtered.map((tg) => (
              <div
                key={tg.id}
                className={`flex items-center gap-2 px-2 py-2 rounded-md text-xs transition-colors ${pick === tg.name ? "bg-primary/10 border border-primary" : "hover:bg-accent border border-transparent"}`}
              >
                <button
                  type="button"
                  onClick={() => setPick(tg.name)}
                  className="flex-1 min-w-0 flex items-center justify-between gap-2 text-left"
                  data-testid={`tag-picker-item-${tg.name}`}
                >
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 truncate">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tg.color }} />
                      <span className="truncate">{tg.name}</span>
                      {tg.shared_with?.length > 0 ? (
                        <Users size={11} className="text-blue-500 shrink-0" title={tg.shared_with.join(", ")} />
                      ) : null}
                    </span>
                    <span className="block pl-4 text-[10px] text-muted-foreground font-mono" data-testid={`tag-picker-created-${tg.name}`}>
                      {fmtDate(tg.created_at)}
                    </span>
                  </span>
                  <span className="font-mono text-muted-foreground shrink-0">{tg.product_count}</span>
                </button>
                {tg.is_owner ? (
                  <button
                    type="button"
                    onClick={() => setShareTag(tg)}
                    className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground shrink-0"
                    title={t("tags.share")}
                    data-testid={`tag-picker-share-${tg.name}`}
                  >
                    <Share2 size={13} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => onConfirm?.(pick)}
              disabled={!pick || busy}
              data-testid="tag-picker-confirm"
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShareTagDialog tag={shareTag} onClose={() => setShareTag(null)} onSaved={load} />
    </>
  );
}

export function ShareTagDialog({ tag, onClose, onSaved }) {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tag) return;
    setPicked(tag.shared_with || []);
    api.get("/tags/shareable-users")
      .then(({ data }) => setUsers(data || []))
      .catch(() => setUsers([]));
  }, [tag]);

  const toggle = (email) =>
    setPicked((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/tags/${tag.id}/share`, { emails: picked });
      toast.success(t("tags.shareSaved"));
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tags.shareFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tag} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="tag-share-dialog">
        <DialogHeader>
          <DialogTitle>{t("tags.shareTitle")} · {tag?.name}</DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground">{t("tags.shareHint")}</p>
        <div className="space-y-1 max-h-64 overflow-y-auto -mx-1 px-1">
          {users.length === 0 ? (
            <p className="text-xs text-muted-foreground px-2 py-6 text-center">{t("tags.noUsers")}</p>
          ) : users.map((u) => (
            <label
              key={u.email}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-accent cursor-pointer"
              data-testid={`tag-share-user-${u.email}`}
            >
              <input
                type="checkbox"
                checked={picked.includes(u.email)}
                onChange={() => toggle(u.email)}
                className="accent-primary"
              />
              <span className="truncate">{u.name}</span>
              <span className="text-muted-foreground truncate">{u.email}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="tag-share-confirm">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
